var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePackingState } from '../../../hooks/packing';
import { ShipmentDetails } from './info/ShipmentDetails';
import { SourceItemList } from './items/SourceItemList';
export var RightSidebar = function (_a) {
    var onShowAddressModal = _a.onShowAddressModal, onShowAttachmentsModal = _a.onShowAttachmentsModal;
    var t = useTranslation().t;
    var _b = useState('details'), activeTab = _b[0], setActiveTab = _b[1];
    var _c = usePackingState(), shipmentItemSourceList = _c.shipmentItemSourceList, shipmentItemDetailList = _c.shipmentItemDetailList, productSkuUpcList = _c.productSkuUpcList, kitComponentProducts = _c.kitComponentProducts;
    var serializedProductIds = useMemo(function () {
        var normalizedItemDetailList = shipmentItemDetailList || [];
        var normalizedSkuUpcList = productSkuUpcList || [];
        var serializedIds = new Set();
        normalizedSkuUpcList.forEach(function (item) {
            if ((item === null || item === void 0 ? void 0 : item.serialized) === 'Y' && (item === null || item === void 0 ? void 0 : item.productId)) {
                serializedIds.add(item.productId);
            }
        });
        normalizedItemDetailList.forEach(function (item) {
            if ((item === null || item === void 0 ? void 0 : item.serialized) === 'Y' && (item === null || item === void 0 ? void 0 : item.productId)) {
                serializedIds.add(item.productId);
            }
        });
        return serializedIds;
    }, [shipmentItemDetailList, productSkuUpcList]);
    var sourceItems = useMemo(function () {
        var normalizedSourceList = shipmentItemSourceList || [];
        var normalizedSkuUpcList = productSkuUpcList || [];
        var normalizedKitComponents = kitComponentProducts || [];
        var byOrderProduct = new Map();
        var parentProductByOrderItemSeqId = new Map();
        var kitChildrenByParentKey = new Map();
        normalizedSourceList.forEach(function (item) {
            var key = "order:" + item.productId;
            var product = normalizedSkuUpcList.find(function (pro) { return pro.productId === item.productId; });
            if (item.orderItemSeqId) {
                parentProductByOrderItemSeqId.set(item.orderItemSeqId, item.productId);
            }
            var existing = byOrderProduct.get(key);
            if (existing) {
                existing.quantity += Number(item.quantity || 0);
                existing.remaining += Number(item.quantityNotHandled || 0);
                existing.packed = Math.max(existing.quantity - existing.remaining, 0);
                return;
            }
            var quantity = Number(item.quantity || 0);
            var remaining = Number(item.quantityNotHandled || 0);
            byOrderProduct.set(key, {
                key: key,
                kind: 'order',
                productId: item.productId,
                productName: (product === null || product === void 0 ? void 0 : product.productName) || item.productId,
                productSku: (product === null || product === void 0 ? void 0 : product.productSku) || '',
                productUpc: (product === null || product === void 0 ? void 0 : product.productUpc) || '',
                isSerialized: serializedProductIds.has(item.productId),
                quantity: quantity,
                remaining: remaining,
                packed: Math.max(quantity - remaining, 0)
            });
        });
        normalizedKitComponents.forEach(function (item) {
            var componentId = item.componentProductId;
            if (!componentId)
                return;
            var resolvedParentProductId = (item.orderItemSeqId && parentProductByOrderItemSeqId.get(item.orderItemSeqId)) ||
                item.productId ||
                '';
            var parentKey = resolvedParentProductId
                ? "order:" + resolvedParentProductId
                : 'order:unknown';
            var key = "kit:" + (resolvedParentProductId || 'unknown') + ":" + componentId;
            var quantity = Number(item.totalQty || 0);
            var packed = Number(item.quantityTotal || 0);
            var remaining = Math.max(quantity - packed, 0);
            var parentChildren = kitChildrenByParentKey.get(parentKey) || new Map();
            var existing = parentChildren.get(key);
            if (existing) {
                existing.quantity += quantity;
                existing.packed += packed;
                existing.remaining += remaining;
                parentChildren.set(key, existing);
                kitChildrenByParentKey.set(parentKey, parentChildren);
                return;
            }
            parentChildren.set(key, {
                key: key,
                kind: 'kit',
                productId: componentId,
                productName: item.productName || componentId,
                productSku: item.sku || '',
                productUpc: item.upc || '',
                isSerialized: serializedProductIds.has(componentId),
                quantity: quantity,
                packed: packed,
                remaining: remaining
            });
            kitChildrenByParentKey.set(parentKey, parentChildren);
        });
        var orderedParents = Array.from(byOrderProduct.values()).map(function (parent) {
            var _a;
            return (__assign(__assign({}, parent), { children: Array.from(((_a = kitChildrenByParentKey.get(parent.key)) === null || _a === void 0 ? void 0 : _a.values()) || []) }));
        });
        var orphanParentEntries = Array.from(kitChildrenByParentKey.entries()).filter(function (_a) {
            var parentKey = _a[0];
            return !byOrderProduct.has(parentKey);
        });
        var orphanParents = orphanParentEntries.map(function (_a) {
            var parentKey = _a[0], childrenMap = _a[1];
            var parentProductId = parentKey.replace('order:', '');
            var product = normalizedSkuUpcList.find(function (pro) { return pro.productId === parentProductId; });
            var children = Array.from(childrenMap.values());
            var quantity = children.reduce(function (sum, child) { return sum + child.quantity; }, 0);
            var remaining = children.reduce(function (sum, child) { return sum + child.remaining; }, 0);
            return {
                key: parentKey,
                kind: 'order',
                productId: parentProductId || 'unknown',
                productName: (product === null || product === void 0 ? void 0 : product.productName) || parentProductId || t('Unknown'),
                productSku: (product === null || product === void 0 ? void 0 : product.productSku) || '',
                productUpc: (product === null || product === void 0 ? void 0 : product.productUpc) || '',
                isSerialized: serializedProductIds.has(parentProductId),
                quantity: quantity,
                remaining: remaining,
                packed: Math.max(quantity - remaining, 0),
                children: children
            };
        });
        return __spreadArray(__spreadArray([], orderedParents, true), orphanParents, true);
    }, [shipmentItemSourceList, productSkuUpcList, kitComponentProducts, serializedProductIds, t]);
    return (_jsxs("div", __assign({ className: 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full min-h-0 w-full overflow-hidden' }, { children: [_jsxs("div", __assign({ className: 'flex gap-2 p-2 bg-gray-50 border-b border-gray-200 rounded-t-md' }, { children: [_jsx("button", __assign({ type: 'button', className: "flex-1 rounded-md px-2 py-2 text-xs font-bold tracking-wider transition-colors " + (activeTab === 'details'
                            ? 'bg-white text-primary shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'), onClick: function () { return setActiveTab('details'); } }, { children: t('Details') }), void 0), _jsx("button", __assign({ type: 'button', className: "flex-1 rounded-md px-2 py-2 text-xs font-bold tracking-wider transition-colors " + (activeTab === 'source'
                            ? 'bg-white text-primary shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'), onClick: function () { return setActiveTab('source'); } }, { children: t('Items') }), void 0)] }), void 0), activeTab === 'details' ? (_jsx("div", __assign({ className: 'flex-1 min-h-0 overflow-hidden' }, { children: _jsx(ShipmentDetails, { onShowAddressModal: onShowAddressModal, onShowAttachmentsModal: onShowAttachmentsModal }, void 0) }), void 0)) : (_jsx("div", __assign({ className: 'flex-1 min-h-0 overflow-hidden' }, { children: _jsx(SourceItemList, { items: sourceItems }, void 0) }), void 0))] }), void 0));
};
export default RightSidebar;
